import type {
  RepositoryPickerRepository$data as Repository,
  RepositoryPickerRepository$key,
} from '@github-ui/item-picker/RepositoryPickerRepository.graphql'
import {useState, useCallback, useId, useEffect, Suspense} from 'react'
import {graphql, readInlineData, usePreloadedQuery, useQueryLoader, type PreloadedQuery} from 'react-relay'
import {Dialog, Button, Textarea} from '@primer/react'
import {CopilotIcon} from '@primer/octicons-react'
import {RepositoryFragment, RepositoryPicker, TopRepositories} from '@github-ui/item-picker/RepositoryPicker'
import styles from './CopilotRefineIssueDialog.module.css'
import type {RepositoryPickerTopRepositoriesQuery} from '@github-ui/item-picker/RepositoryPickerTopRepositoriesQuery.graphql'
import {useSearch} from './use-search'
import type {CopilotRefineIssueDialogQuery} from './__generated__/CopilotRefineIssueDialogQuery.graphql'

export interface CopilotRefineIssueDialogProps {
  title?: string
  markdown: string
  copilotApiUrl: string
  owner?: string
  repoName?: string
  onClose: () => void
}

const DEFAULT_TITLE = 'Issue title is not set'

const extractFilenameFromPath = (path: string) => {
  const parts = path.split('/')
  return parts[parts.length - 1]
}

export const CopilotRefineIssueDialogGraphQLQuery = graphql`
  query CopilotRefineIssueDialogQuery($owner: String!, $name: String!) {
    repository(owner: $owner, name: $name) {
      ...RepositoryPickerRepository
    }
  }
`

export function CopilotRefineIssueDialog(props: CopilotRefineIssueDialogProps) {
  const [topReposQueryRef, loadTopRepos, disposeTopRepos] =
    useQueryLoader<RepositoryPickerTopRepositoriesQuery>(TopRepositories)

  const [currentRepoQueryRef, loadCurrentRepo, disposeCurrentRepo] = useQueryLoader<CopilotRefineIssueDialogQuery>(
    CopilotRefineIssueDialogGraphQLQuery,
  )

  useEffect(() => {
    loadTopRepos({topRepositoriesFirst: 5, hasIssuesEnabled: true, owner: null}, {fetchPolicy: 'store-or-network'})
    return () => {
      disposeTopRepos()
    }
  }, [disposeTopRepos, loadTopRepos])

  useEffect(() => {
    if (props.owner && props.repoName) {
      loadCurrentRepo({owner: props.owner, name: props.repoName}, {fetchPolicy: 'store-or-network'})
    }

    return () => disposeCurrentRepo()
  }, [disposeCurrentRepo, loadCurrentRepo, props.owner, props.repoName])

  if (!topReposQueryRef || !currentRepoQueryRef) {
    return null // Return nothing while loading instead of showing a partial dialog
  }

  return (
    <Suspense>
      <CopilotRefineIssueDialogInternal
        {...props}
        topReposQueryRef={topReposQueryRef}
        currentRepoQueryRef={currentRepoQueryRef}
      />
    </Suspense>
  )
}

interface CopilotRefineIssueDialogInternalProps extends CopilotRefineIssueDialogProps {
  topReposQueryRef: PreloadedQuery<RepositoryPickerTopRepositoriesQuery>
  currentRepoQueryRef: PreloadedQuery<CopilotRefineIssueDialogQuery>
}

function CopilotRefineIssueDialogInternal({
  title = DEFAULT_TITLE,
  markdown,
  copilotApiUrl,
  onClose,
  topReposQueryRef,
  currentRepoQueryRef,
}: CopilotRefineIssueDialogInternalProps) {
  const currentRepoData = usePreloadedQuery<CopilotRefineIssueDialogQuery>(
    CopilotRefineIssueDialogGraphQLQuery,
    currentRepoQueryRef,
  )

  const repository =
    currentRepoData && currentRepoData.repository !== undefined
      ? // eslint-disable-next-line no-restricted-syntax
        readInlineData<RepositoryPickerRepository$key>(RepositoryFragment, currentRepoData.repository) || undefined
      : null

  const [selectedRepository, setSelectedRepository] = useState<Repository | undefined>(repository ?? undefined)
  const {isSearching, searchError, search, cancelSearch, suggestedSnippets} = useSearch(title, markdown, copilotApiUrl)
  const [snippetsMarkdown, setSnippetsMarkdown] = useState<string>('')
  const isLoading = isSearching

  const handleSearch = useCallback(async () => {
    if (!isLoading && selectedRepository) {
      await search({
        repository: selectedRepository,
      })
    }
  }, [isLoading, search, selectedRepository])

  useEffect(() => {
    handleSearch()

    return cancelSearch
    // eslint-disable-next-line react-hooks/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const descriptionId = useId()

  useEffect(() => {
    const m = suggestedSnippets
      .filter(snippet => snippet.type === 'github.snippet')
      .map(
        snippet => `**${extractFilenameFromPath(snippet.data.path)}:**
${snippet.data.url}

\`\`\`${snippet.data.languageName}
${snippet.data.contents}
\`\`\`
`,
      )
      .join('\n\n')

    setSnippetsMarkdown(m)
  }, [suggestedSnippets])

  return (
    <Dialog
      title="Refine issue"
      onClose={onClose}
      width="xlarge"
      className={styles.dialog}
      renderBody={() => (
        <div className={styles.dialogBody}>
          <div className={styles.repositoryPicker}>
            <h2 className={styles.repositoryPickerHeader}>Code repository</h2>
            <RepositoryPicker
              aria-describedby={descriptionId}
              initialRepository={selectedRepository}
              onSelect={setSelectedRepository}
              organization={repository?.owner.login}
              topReposQueryRef={topReposQueryRef}
              focusRepositoryPicker
              enforceAtleastOneSelected
              options={{hasIssuesEnabled: true}}
            />
          </div>
          <div className={styles.files}>
            <h2 className={styles.filesHeader}>Relevant files</h2>
            <div className={styles.textareaContainer}>
              <Textarea
                aria-label="Relevant files"
                id="relevant-files"
                value={snippetsMarkdown}
                onChange={e => setSnippetsMarkdown(e.target.value)}
                contrast
                rows={16}
                block
                resize="vertical"
                className="textarea"
              />
            </div>
          </div>
        </div>
      )}
      renderFooter={() => (
        <div className={styles.footer}>
          <div className={styles.footerSearch}>
            {!isLoading && <Button onClick={handleSearch}>Try again</Button>}
            {isLoading && (
              <div className={styles.loading}>
                <CopilotIcon />
                <span>Copilot is finding related files...</span>
              </div>
            )}
            {searchError && <div className="color-fg-danger">{searchError}</div>}
          </div>
          <div className={styles.footerActions}>
            <Button variant="default" onClick={cancelSearch} disabled={!isLoading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={isLoading}
              onClick={() => navigator.clipboard.writeText(snippetsMarkdown)}
            >
              Copy
            </Button>
          </div>
        </div>
      )}
    />
  )
}

try{ CopilotRefineIssueDialog.displayName ||= 'CopilotRefineIssueDialog' } catch {}
try{ CopilotRefineIssueDialogInternal.displayName ||= 'CopilotRefineIssueDialogInternal' } catch {}